import _ from 'underscore'

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, NavLink } from 'react-router-dom'

import PropTypes from 'prop-types'
import styled from 'styled-components'

import Search from 'components/Search'
import Spinner from 'components/Spinner'

import SingleDeviceResult from 'pages/DeviceSearch/SingleDeviceResult'
import DevicesList from 'pages/DeviceSearch/MultipleDevicesResults/List'

import useDeviceSearch from 'hooks/useDeviceSearch'

import { color } from 'styles/colors'
import { NoData } from 'styles/shared'

const propTypes = {
  favorites: PropTypes.arrayOf(PropTypes.string).isRequired
}

const FavoritesList = ({ favorites }) => {
  const navigate = useNavigate()
  const { id: deviceId } = useParams()

  const { devices, areDevicesLoaded } = useDeviceSearch(favorites)

  const [query, setQuery] = useState(deviceId)
  const [unmatchedQueryItems, setUnmatchedQueryItems] = useState([])
  const [filteredDevices, setFilteredDevices] = useState([])
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    if (areDevicesLoaded) {
      const loadedDevices = devices ?? []

      if (_.isEmpty(deviceId)) {
        setFilteredDevices(loadedDevices)
      } else {
        const matchingDevices = _.filter(loadedDevices, (device) => device.deviceId === deviceId)
        setFilteredDevices(matchingDevices)

        if (!matchingDevices.length) {
          setUnmatchedQueryItems([deviceId])
        }
      }
    }
  }, [areDevicesLoaded, deviceId, devices])

  const onSearch = useCallback((newQuery) => {
    setShowSpinner(true)
    const queryItems = newQuery.split(',')
      .map(queryItem => queryItem.trim())
      .filter(Boolean)

    if (queryItems.length === 0) {
      setFilteredDevices(devices)
      setUnmatchedQueryItems([])
      setShowSpinner(false)
      navigate('/favorites')

      return
    }

    const lowerCasedQueryItems = queryItems.map(queryItem => queryItem.toLowerCase())

    const matchingDevices = devices.filter(device => (
      lowerCasedQueryItems.includes(device.deviceId.toLowerCase()) ||
      (lowerCasedQueryItems.includes(device.serialNumber?.toLowerCase()))
    ))

    if (matchingDevices.length === filteredDevices.length && !_.difference(matchingDevices, filteredDevices).length) {
      setShowSpinner(false)
      return
    }

    setFilteredDevices(matchingDevices)

    const unmatchedQueryItems = queryItems.filter(queryItem => (
      !devices.map(device => device.deviceId.toLowerCase()).includes(queryItem.toLowerCase())) &&
      !devices.map(device => device.serialNumber?.toLowerCase()).includes(queryItem.toLowerCase())
    )
    setUnmatchedQueryItems(unmatchedQueryItems)
    setShowSpinner(false)
    navigate('/favorites')
  }, [devices, filteredDevices, navigate])

  const onClickClearIcon = () => {
    setShowSpinner(false)
    setQuery('')
    onSearch('')
  }

  if (!areDevicesLoaded) {
    return <Spinner variant="page" />
  }

  const showSingleDevice = filteredDevices?.length === 1 && unmatchedQueryItems.length === 0

  return (
    <>
      {favorites && favorites.length > 0 && (
        <Search
          focusOnMount
          value={query}
          placeholder="Device AUID or Serial Number"
          helperText="Search from favorites list below."
          ariaLabel="favorites"
          onChange={setQuery}
          onClickClearIcon={onClickClearIcon}
          showSpinner={showSpinner}
          {...{ onSearch }}
        />
      )}

      {unmatchedQueryItems.length > 0 && (
        <>
          <S.UnmatchedQueryItems>Devices not found: <span>{unmatchedQueryItems.join(', ')}</span></S.UnmatchedQueryItems>
          <S.UnmatchedMessage>For the above search entries, the AUID or Serial Number entered is not recognized.</S.UnmatchedMessage>
        </>
      )}

      {favorites && favorites.length < 1 && (
        <NoData>
          <h1>No favorites</h1>
          <S.NoDataDescription>Go to the <NavLink to="/devices">Device Search</NavLink> and search against a single AUID / serial number or enter multiple values comma separated and select to add devices to the Favorites List.</S.NoDataDescription>
        </NoData>
      )}

      {showSingleDevice && <SingleDeviceResult device={filteredDevices[0]} pathRoot="favorites" /> }
      {!showSingleDevice && filteredDevices?.length > 0 && (
        <>
          <DevicesList
            pathRoot="favorites"
            devices={filteredDevices}
            {...{ favorites }}
          />
        </>
      )}
    </>
  )
}

FavoritesList.propTypes = propTypes

export default FavoritesList

const S = {
  Header: styled.h2`
    margin-top: 40px;
  `,
  UnmatchedQueryItems: styled.div`
    font-weight: bold;
    margin-top: 30px;

    > span {
      color: ${color('errorText')};
    }
  `,
  UnmatchedMessage: styled.div`
    margin-bottom: 30px;
  `,
  NoDataDescription: styled.h2`
    max-width: 650px;
    line-height: 25px;
  `
}
